import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { environment } from "src/environments/environment";
import { NotifierService } from "../../../_services/notifier.service";

interface ApprovalWorkflowConfig {
  account_id: string;
  service_name: string;
  approval_level: number;
  approval_level_name: string;
  approver_email: string;
  approver_name: string;
  is_active: boolean;
}

interface ApprovalWorkflowForm {
  approval_level: number;
  approval_level_name: string;
  approver_email: string;
  approver_name: string;
}

type FormMode = "add" | "edit";

@Component({
  templateUrl: "./approval-config.settings.component.html",
  styleUrls: ["./approval-config.settings.component.css"],
})
export class ApprovalConfigSettingsComponent implements OnInit, OnDestroy {
  accountId = localStorage.getItem("accountId") || "";
  serviceOptions: string[] = ["EC2", "EKS"];
  selectedService = "EC2";
  configs: ApprovalWorkflowConfig[] = [];
  loading = false;
  saving = false;
  errorMessage = "";
  currentMessage: any;
  showFormModal = false;
  showConfirmModal = false;
  formMode: FormMode = "add";
  pendingConfig: ApprovalWorkflowConfig = null;
  form: ApprovalWorkflowForm = this.getEmptyForm();
  writeAccess: boolean =
    localStorage.getItem("acT") == "readandwrite" ||
    localStorage.getItem("role") == "Admin" ||
    localStorage.getItem("ut") == "admin";

  private endpoint = `${environment.apiURL}/admin/apis/approval-configuration/approval-workflow`;

  constructor(
    private http: HttpClient,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      let data: any = {};
      try {
        data = JSON.parse(msg);
      } catch (error) {
        return;
      }

      if (data.value == null || data.key != "accountId") {
        return;
      }

      this.accountId = data.value;
      this.load();
    });

    this.load();
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  async load(): Promise<void> {
    if (!this.isValidAccountId()) {
      this.configs = [];
      this.errorMessage = "Select a valid 12-digit AWS account to configure approvals.";
      return;
    }

    this.loading = true;
    this.errorMessage = "";

    const params = new HttpParams()
      .set("account_id", this.accountId)
      .set("service_name", this.selectedService);

    try {
      const result = await this.http
        .get<ApprovalWorkflowConfig[]>(this.endpoint, {
          headers: this.getHeaders(),
          params,
        })
        .toPromise();

      this.configs = this.mergeWithCachedDisabledConfigs(
        Array.isArray(result) ? result : []
      );
    } catch (error) {
      this.configs = [];
      this.errorMessage = this.getErrorMessage(
        error,
        "Unable to fetch approval configuration."
      );
      this.notifier.alert("Error", "", this.errorMessage, "error", 5000);
    } finally {
      this.loading = false;
    }
  }

  onServiceChange(): void {
    this.closeFormModal();
    this.closeConfirmModal();
    this.load();
  }

  openAddModal(): void {
    if (!this.writeAccess || !this.canAddLevel()) {
      return;
    }

    this.formMode = "add";
    this.form = this.getEmptyForm();
    this.form.approval_level = this.nextApprovalLevel();
    this.showFormModal = true;
  }

  openEditModal(config: ApprovalWorkflowConfig): void {
    if (!this.writeAccess) {
      return;
    }

    this.formMode = "edit";
    this.pendingConfig = config;
    this.form = {
      approval_level: config.approval_level,
      approval_level_name: config.approval_level_name,
      approver_email: config.approver_email,
      approver_name: config.approver_name,
    };
    this.showFormModal = true;
  }

  closeFormModal(): void {
    this.showFormModal = false;
    this.pendingConfig = null;
    this.form = this.getEmptyForm();
  }

  openConfirmModal(config: ApprovalWorkflowConfig): void {
    if (!this.writeAccess || !this.canToggleStatus(config)) {
      return;
    }

    this.pendingConfig = config;
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
    this.pendingConfig = null;
  }

  async submitForm(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    this.saving = true;

    const payload = {
      account_id: this.accountId,
      service_name: this.selectedService,
      approval_level: this.form.approval_level,
      approval_level_name: this.form.approval_level_name.trim(),
      approver_email: this.form.approver_email.trim(),
      approver_name: this.form.approver_name.trim(),
    };

    try {
      if (this.formMode == "add") {
        await this.http
          .post<ApprovalWorkflowConfig>(this.endpoint, payload, {
            headers: this.getHeaders(),
          })
          .toPromise();
        this.notifier.alert("Success", "", "Approval level added successfully.", "success", 5000);
      } else {
        const isActive = this.pendingConfig ? this.pendingConfig.is_active : true;
        const updatePayload = { ...payload, is_active: isActive };
        await this.http
          .put<ApprovalWorkflowConfig>(
            this.endpoint,
            updatePayload,
            { headers: this.getHeaders() }
          )
          .toPromise();
        if (updatePayload.is_active) {
          this.removeCachedDisabledConfig(updatePayload);
        } else {
          this.upsertCachedDisabledConfig(updatePayload);
        }
        this.notifier.alert("Success", "", "Approval level updated successfully.", "success", 5000);
      }

      this.closeFormModal();
      await this.load();
    } catch (error) {
      const fallback =
        this.formMode == "add"
          ? "Unable to add approval level."
          : "Unable to update approval level.";
      this.notifier.alert("Error", "", this.getErrorMessage(error, fallback), "error", 5000);
    } finally {
      this.saving = false;
    }
  }

  async confirmStatusChange(): Promise<void> {
    if (!this.pendingConfig) {
      return;
    }

    this.saving = true;
    const payload = {
      account_id: this.pendingConfig.account_id,
      service_name: this.pendingConfig.service_name,
      approval_level: this.pendingConfig.approval_level,
      approval_level_name: this.pendingConfig.approval_level_name,
      approver_email: this.pendingConfig.approver_email,
      approver_name: this.pendingConfig.approver_name,
      is_active: !this.pendingConfig.is_active,
    };

    try {
      await this.http
        .put<ApprovalWorkflowConfig>(this.endpoint, payload, {
          headers: this.getHeaders(),
        })
        .toPromise();

      if (payload.is_active) {
        this.removeCachedDisabledConfig(payload);
      } else {
        this.upsertCachedDisabledConfig(payload);
      }

      const action = payload.is_active ? "enabled" : "disabled";
      this.notifier.alert("Success", "", `Approval level ${action} successfully.`, "success", 5000);
      this.closeConfirmModal();
      await this.load();
    } catch (error) {
      this.notifier.alert(
        "Error",
        "",
        this.getErrorMessage(error, "Unable to update approval level status."),
        "error",
        5000
      );
    } finally {
      this.saving = false;
    }
  }

  canAddLevel(): boolean {
    return this.configs.every((config) => config.is_active);
  }

  canToggleStatus(config: ApprovalWorkflowConfig): boolean {
    if (config.is_active) {
      return config.approval_level == this.highestActiveLevel();
    }

    return config.approval_level == this.highestActiveLevel() + 1;
  }

  getToggleTitle(config: ApprovalWorkflowConfig): string {
    return this.getToggleDisabledReason(config);
  }

  getToggleDisabledReason(config: ApprovalWorkflowConfig): string {
    if (!this.writeAccess) {
      return "You need write access to change approval level status.";
    }

    if (this.canToggleStatus(config)) {
      return "";
    }

    return config.is_active
      ? "Disable the highest active level first."
      : "Enable levels sequentially from lowest disabled level.";
  }

  getAddLevelDisabledReason(): string {
    if (!this.writeAccess) {
      return "You need write access to add approval levels.";
    }

    if (!this.isValidAccountId()) {
      return "Select a valid 12-digit AWS account to add approval levels.";
    }

    if (!this.canAddLevel()) {
      return "Enable disabled levels before adding another level.";
    }

    return "";
  }

  getStatusAction(config: ApprovalWorkflowConfig): string {
    return config.is_active ? "Disable" : "Enable";
  }

  getModalTitle(): string {
    return this.formMode == "add" ? "Add Approval Level" : "Edit Approval Level";
  }

  getConfirmTitle(): string {
    return this.pendingConfig && this.pendingConfig.is_active
      ? "Disable Approval Level"
      : "Enable Approval Level";
  }

  getConfirmAction(): string {
    return this.pendingConfig && this.pendingConfig.is_active ? "Disable" : "Enable";
  }

  trackByLevel(index: number, config: ApprovalWorkflowConfig): number {
    return config.approval_level;
  }

  private validateForm(): boolean {
    if (!this.isValidAccountId()) {
      this.notifier.alert("Error", "", "Select a valid 12-digit AWS account.", "error", 5000);
      return false;
    }

    if (!this.form.approval_level_name.trim()) {
      this.notifier.alert("Error", "", "Approval level name is required.", "error", 5000);
      return false;
    }

    if (!this.form.approver_name.trim()) {
      this.notifier.alert("Error", "", "Approver name is required.", "error", 5000);
      return false;
    }

    if (!this.isValidEmail(this.form.approver_email)) {
      this.notifier.alert("Error", "", "Enter a valid approver email.", "error", 5000);
      return false;
    }

    return true;
  }

  private nextApprovalLevel(): number {
    if (this.configs.length == 0) {
      return 1;
    }

    return Math.max(...this.configs.map((config) => config.approval_level)) + 1;
  }

  private highestActiveLevel(): number {
    const activeLevels = this.configs
      .filter((config) => config.is_active)
      .map((config) => config.approval_level);

    return activeLevels.length == 0 ? 0 : Math.max(...activeLevels);
  }

  private getEmptyForm(): ApprovalWorkflowForm {
    return {
      approval_level: 1,
      approval_level_name: "",
      approver_email: "",
      approver_name: "",
    };
  }

  private isValidAccountId(): boolean {
    return /^\d{12}$/.test(this.accountId || "");
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
  }

  private mergeWithCachedDisabledConfigs(
    apiConfigs: ApprovalWorkflowConfig[]
  ): ApprovalWorkflowConfig[] {
    const configsByLevel: { [level: number]: ApprovalWorkflowConfig } = {};

    this.getCachedDisabledConfigs().forEach((config) => {
      configsByLevel[config.approval_level] = config;
    });

    apiConfigs.forEach((config) => {
      configsByLevel[config.approval_level] = config;
      if (config.is_active) {
        this.removeCachedDisabledConfig(config);
      }
    });

    return Object.keys(configsByLevel)
      .map((level) => configsByLevel[Number(level)])
      .sort((first, second) => first.approval_level - second.approval_level);
  }

  private upsertCachedDisabledConfig(config: ApprovalWorkflowConfig): void {
    const disabledConfigs = this.getCachedDisabledConfigs().filter(
      (cachedConfig) => cachedConfig.approval_level != config.approval_level
    );

    disabledConfigs.push({ ...config, is_active: false });
    this.setCachedDisabledConfigs(disabledConfigs);
  }

  private removeCachedDisabledConfig(config: ApprovalWorkflowConfig): void {
    const disabledConfigs = this.getCachedDisabledConfigs().filter(
      (cachedConfig) => cachedConfig.approval_level != config.approval_level
    );

    this.setCachedDisabledConfigs(disabledConfigs);
  }

  private getCachedDisabledConfigs(): ApprovalWorkflowConfig[] {
    const cachedValue = localStorage.getItem(this.getDisabledCacheKey());

    if (!cachedValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(cachedValue);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  }

  private setCachedDisabledConfigs(configs: ApprovalWorkflowConfig[]): void {
    if (configs.length == 0) {
      localStorage.removeItem(this.getDisabledCacheKey());
      return;
    }

    localStorage.setItem(this.getDisabledCacheKey(), JSON.stringify(configs));
  }

  private getDisabledCacheKey(): string {
    return `approvalConfigDisabled:${this.accountId}:${this.selectedService}`;
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders().set("Content-Type", "application/json; charset=utf-8");
    const token = localStorage.getItem("t");

    if (token) {
      headers = headers.set("Authorization", token);
    }

    return headers;
  }

  private getErrorMessage(error: any, fallback: string): string {
    const body = error && error.error ? error.error : error;

    if (body && Array.isArray(body.detail) && body.detail.length > 0) {
      return body.detail
        .map((item: any) => item && item.msg ? item.msg : "")
        .filter((message: string) => message.length > 0)
        .join(", ") || fallback;
    }

    if (body && typeof body == "object") {
      return body.detail || body.error || body.msg || body.message || fallback;
    }

    if (typeof body == "string" && body.trim().length > 0) {
      return body;
    }

    return fallback;
  }
}
